import React, { useMemo, useState } from "react";

import { Plus } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/src/components/ui/button";

import AdminRoleDialog from "./AdminRoleDialog";
import { ADMIN_ROLE_SCOPES } from "./adminRoleScopes";
import {
  adminRoleColumns,
  getAdminRoleRows,
} from "./tableParams/adminRoleColumns";
import AdminRoleByScope from "../../models/AdminRoleByScope";
import DataTable from "../dataTable/DataTable";

import type { JSX } from "react";

import type { AdminRoleSubmitValues } from "./AdminRoleDialog.types";
import type { AdminRoleScope } from "./adminRoleScopes.types";
import type { DialogState } from "./AdminRoleTable.types";
import type {
  DeleteRolesMutationFnParams,
  UpdateRolesMutationFnParams,
} from "./queries/mutations";
import type { AdminRoleRow } from "./tableParams/adminRoleColumns.types";
import type AdminUserRoles from "../../models/AdminUserRoles";

interface AdminRoleTableProps {
  scope: AdminRoleScope;
  userId: string;
  roles: AdminUserRoles;
  onUpdateRoles: (params: UpdateRolesMutationFnParams) => void;
  onDeleteRoles: (params: DeleteRolesMutationFnParams) => void;
  isLoading?: boolean;
  isError?: boolean;
  onRetry: () => unknown;
}

function AdminRoleTable({
  scope,
  userId,
  roles,
  onUpdateRoles,
  onDeleteRoles,
  isLoading,
  isError,
  onRetry,
}: AdminRoleTableProps): JSX.Element {
  const { t } = useTranslation("editor-users");
  const config = ADMIN_ROLE_SCOPES[scope];
  const [dialog, setDialog] = useState<DialogState>(null);

  const roleRowList = useMemo(
    () => getAdminRoleRows(scope, roles, t),
    [scope, roles, t],
  );

  const columnList = useMemo(
    () =>
      adminRoleColumns({
        t,
        scope,
        onEdit: (row) => setDialog({ editedRow: row }),
        onDelete: (row) =>
          onDeleteRoles({ userId, ...config.toScopeIds(row.uuid) }),
      }),
    [t, scope, config, userId, onDeleteRoles],
  );

  const editedRow = dialog?.editedRow ?? null;
  const scopeList = config.getScopes(roles);
  const otherRoleList = [
    ...roles.projectList,
    ...roles.channelList,
    ...roles.organizationList.filter(
      (organization) => organization.uuid !== editedRow?.uuid,
    ),
  ].flatMap((roleScope) => roleScope.roleList);

  const handleSubmit = ({
    uuid,
    name,
    roles: frontEndRoles,
    isNew,
  }: AdminRoleSubmitValues): void => {
    onUpdateRoles({
      userId,
      ...config.toScopeIds(uuid),
      roles: AdminRoleByScope.convertRolesToBackend(frontEndRoles),
      name,
      newRole: isNew,
    });
    setDialog(null);
  };

  const handleRowClick = (row: AdminRoleRow): void => {
    setDialog({ editedRow: row });
  };

  const addButton = (
    <Button
      disabled={!config.canAdd(roles)}
      onClick={() => setDialog({ editedRow: null })}
      size="lg"
      type="button"
    >
      <Plus />
      {t("addRole")}
    </Button>
  );

  return (
    <React.Fragment>
      <DataTable
        compactPagination
        columns={columnList}
        data={roleRowList}
        emptyText={t("noRoles")}
        headerActions={addButton}
        isError={isError}
        isLoading={isLoading}
        onRetry={onRetry}
        onRowClick={config.coversAll(roles) ? undefined : handleRowClick}
        title={t(config.titleKey)}
        urlSync={{ prefix: scope }}
      />
      <AdminRoleDialog
        editedRow={editedRow}
        excludedIds={scopeList.map((roleScope) => roleScope.uuid)}
        isOpen={dialog !== null}
        onClose={() => setDialog(null)}
        onSubmit={handleSubmit}
        otherRoles={otherRoleList}
        scope={scope}
      />
    </React.Fragment>
  );
}

export default AdminRoleTable;
